import {
  EVENT_REGISTRY,
  INTEGRATION_EVENT_NAMES,
  payloadFields,
  producersOf,
  consumersOf,
  type PayloadFieldInfo,
} from '../integration/registry.js';

/**
 * The event catalogue — a pure PROJECTION of the central registry (`../integration/registry.ts`), so
 * it cannot drift: names come from the roster, the payload fields are introspected from each event's
 * Zod schema, and producers/consumers are derived from the service manifest. Nothing here is
 * hand-authored twice. Consumed by the Hub Monitor's Catalogue page via `GET /api/hub/events`.
 */
export type EventPayloadField = PayloadFieldInfo;

export interface EventCatalogueEntry {
  readonly name: string;
  readonly title: string;
  readonly topic: string;
  readonly ordered: boolean;
  readonly description: string;
  readonly producers: readonly string[];
  readonly consumers: readonly string[];
  readonly payload: readonly EventPayloadField[];
}

/** Every published integration event, projected for the catalogue. */
export const EVENT_CATALOGUE: readonly EventCatalogueEntry[] = Object.freeze(
  INTEGRATION_EVENT_NAMES.map((name): EventCatalogueEntry => {
    const def = EVENT_REGISTRY[name];
    return {
      name,
      title: def.title,
      topic: def.topic,
      ordered: def.ordered,
      description: def.description,
      producers: producersOf(name),
      consumers: consumersOf(name),
      payload: payloadFields(name),
    };
  }),
);

/** ISO date the catalogue projection was last revised (bump when the registry's event set changes). */
export const EVENT_CATALOGUE_UPDATED = '2026-07-29';

/** Look up a catalogued event by its integration name (undefined if not catalogued). */
export function eventByName(name: string): EventCatalogueEntry | undefined {
  return EVENT_CATALOGUE.find((e) => e.name === name);
}
